import {useQuery, useQueryClient} from '@github-ui/react-query'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useAlive} from '@github-ui/use-alive'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {InboxIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useCallback, useMemo} from 'react'

import {NavLinkItem} from '../../primitives/NavItemLink'
import styles from './styles.module.css'

export const NOTIFICATIONS_INDICATOR_URL = '/notifications/indicator'
const RETRY_DELAY = 500 // ms
const MAX_RETRIES = 3

export type NotificationIndicatorMode = 'none' | 'global' | 'disabled' | 'unavailable'

const AriaLabelForMode: Record<NotificationIndicatorMode, string> = {
  none: 'You have no unread notifications',
  global: 'You have unread notifications',
  disabled: 'Notifications are disabled',
  unavailable: 'Notifications are unavailable at the moment.',
}

export interface NotificationItemProps {
  /** The default notification indicator mode */
  indicatorMode?: NotificationIndicatorMode

  /** The WebSocket channel to listen for notifications */
  websocketChannel?: string

  /** The URL to fetch the initial notification indicator state from */
  fetchIndicatorSrc?: string

  /** Whether to fetch the initial notification indicator state from `fetchIndicatorSrc` */
  fetchIndicatorEnabled?: boolean

  /**
   * The delay before retrying a failed fetch
   * @default 500ms
   */
  retryDelay?: number

  /** The maximum number of retries for a failed fetch
   * @default 3
   */
  maxRetries?: number
}

type AsyncNotificationIndicatorConfig = {
  defaultIndicatorMode?: NotificationIndicatorMode
  src: string
  enabled: boolean
} & Pick<NotificationItemProps, 'retryDelay' | 'maxRetries'>

interface AsyncNotificationIndicatorResult {
  mode: NotificationIndicatorMode
}

interface NotificationSocketMessage {
  indicator_mode: NotificationIndicatorMode
}

function useAsyncNotificationIndicator(config: AsyncNotificationIndicatorConfig) {
  const queryClient = useQueryClient()

  const queryData = useQuery<AsyncNotificationIndicatorResult>({
    enabled: config.enabled,
    queryKey: ['global-nav-notifications', config.src],
    initialData: {mode: config.defaultIndicatorMode ?? 'none'},
    refetchOnMount: config.enabled ? 'always' : false,
    queryFn: async () => {
      const response = await verifiedFetchJSON(config.src)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json() as Promise<AsyncNotificationIndicatorResult>
    },
    retry: config.maxRetries ?? MAX_RETRIES,
    retryDelay: config.retryDelay ?? RETRY_DELAY,
  })

  const updateIndicatorMode = useCallback(
    (mode: NotificationIndicatorMode) => {
      queryClient.setQueryData<AsyncNotificationIndicatorResult>(['global-nav-notifications', config.src], oldData => {
        if (!oldData) {
          return oldData
        }

        return {
          ...oldData,
          mode,
        }
      })
    },
    [queryClient, config.src],
  )

  return {
    ...queryData,
    updateIndicatorMode,
  }
}

function useAsyncNotifications({
  defaultIndicatorMode = 'none',
  fetchIndicatorSrc,
  websocketChannel,
  fetchIndicatorEnabled = false,
  maxRetries,
  retryDelay,
}: NotificationItemProps & {
  defaultIndicatorMode?: NotificationIndicatorMode
}): {
  mode: NotificationIndicatorMode
} {
  const {data, isFetching, updateIndicatorMode} = useAsyncNotificationIndicator({
    defaultIndicatorMode,
    src: fetchIndicatorSrc ?? NOTIFICATIONS_INDICATOR_URL,
    enabled: fetchIndicatorEnabled,
    maxRetries,
    retryDelay,
  })

  const onNotification = useCallback(
    (result: NotificationSocketMessage) => {
      updateIndicatorMode(result.indicator_mode)
    },
    [updateIndicatorMode],
  )

  // Only use the websocket for updates if we already have a valid response from the fetch
  const notificationsChannel = useMemo<string | undefined>(() => {
    if (data?.mode === 'disabled' || !websocketChannel || isFetching) {
      return undefined
    }

    return websocketChannel
  }, [isFetching, data, websocketChannel])

  useAlive<NotificationSocketMessage>(notificationsChannel, onNotification)

  // Use useRuntimeEnvironment to avoid hydration mismatches.
  // During SSR and hydration, return the server-provided value (defaultIndicatorMode).
  // After hydration, return the react-query data which may have been cached from a previous navigation.
  const {isBrowser} = useRuntimeEnvironment()
  const mode = isBrowser ? (data.mode ?? 'none') : defaultIndicatorMode

  return {
    mode,
  }
}

export function NotificationItem({
  indicatorMode: defaultIndicatorMode = 'none',
  websocketChannel,
  fetchIndicatorSrc,
  fetchIndicatorEnabled = false,
  maxRetries,
  retryDelay,
}: NotificationItemProps) {
  const {mode} = useAsyncNotifications({
    defaultIndicatorMode,
    websocketChannel,
    fetchIndicatorSrc,
    fetchIndicatorEnabled,
    maxRetries,
    retryDelay,
  })

  if (mode === 'disabled') {
    return null
  }

  return (
    <NavLinkItem
      analyticsKey="NOTIFICATIONS_HEADER"
      href="/notifications"
      aria-label={AriaLabelForMode[mode]}
      commandId="global-navigation:go-to-notifications"
      icon={InboxIcon}
      className={clsx({
        [styles.notificationIndicator]: mode !== 'none',
      })}
    />
  )
}
